import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import HighScore from "../../assets/high-score.png";
import RestartIcon from "../../assets/refresh-arrow.png";
import StarsIcon from "../../assets/sparkling.png";
import { USER_TYPE } from "../../constants/common";
import lessonServices from "../../services/lessonServices";
import { useNavigateWithLang } from "../../utils/navigation";
import DragAndDropGameFinishModal from "../modals/common/DragAndDropGameFinishModal";
import DragAndDropGameStartModal from "../modals/common/DragAndDropGameStartModal";

const DragAndDropGame = ({ gameDetails }) => {
  const { id: lessonID } = useParams();
  const { t } = useTranslation();
  const dragAndDropStartModalRef = useRef();
  const dragAndDropFinishModalRef = useRef();
  const navigate = useNavigateWithLang();

  const [cards, setCards] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [solution, setSolution] = useState([]);
  const [matches, setMatches] = useState({});
  const [draggingCard, setDraggingCard] = useState(null);
  const [userGameId, setUserGameId] = useState(null);
  const [numberOfMoves, setNumberOfMoves] = useState(0);
  const [gameFinished, setGameFinished] = useState(false);
  const [gameStatus, setGameStatus] = useState(null); // 'correct' | 'wrong'

  const handleDrop = (keywordId) => {
    if (draggingCard) {
      const isCorrectMatch = checkCorrect(keywordId, draggingCard.card_id);

      setMatches((prev) => ({
        ...prev,
        [keywordId]: draggingCard.card_id,
      }));

      setDraggingCard(null);

      // Only count the move if it's correct
      if (isCorrectMatch) {
        setNumberOfMoves((prev) => prev + 1);
      }
    }
  };

  const checkCorrect = (keywordId, cardId) => {
    return solution.some((pair) => pair.keyword_id === keywordId && pair.card_id === cardId);
  };

  const shuffleArray = (array) => {
    return [...array].sort(() => Math.random() - 0.5);
  };

  const openFinishModal = useCallback(() => {
    dragAndDropFinishModalRef.current?.openModal();
  }, []);

  useEffect(() => {
    if (keywords.length === 0 || gameFinished) return;

    const allMatched = keywords.every((keyword) => {
      const matchedCardId = matches[keyword.id];
      return matchedCardId && checkCorrect(keyword.id, matchedCardId);
    });

    const allKeywordsMatched = keywords.every((keyword) => matches[keyword.id]);

    if (allKeywordsMatched) {
      setGameFinished(true);
      setGameStatus(allMatched ? "correct" : "wrong");
      openFinishModal();
    }
  }, [matches, keywords, gameFinished]);

  const handleGameCompletion = async () => {
    try {
      await lessonServices.submitGame(userGameId, {
        number_of_moves: numberOfMoves,
        is_completed: true,
      });
      await lessonServices.finishLesson(lessonID);
      navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area`);
    } catch (err) {
      console.error("Failed to submit game:", err);
    }
  };

  const initializeGame = async () => {
    try {
      setMatches({});
      setDraggingCard(null);
      setNumberOfMoves(0);
      setUserGameId(null);
      setCards([]);
      setKeywords([]);
      setSolution([]);
      setGameFinished(false);
      setGameStatus(null);

      const data = await lessonServices.getLessonGameStage(gameDetails?.id);
      setUserGameId(data?.user_game_id);

      setCards(shuffleArray(data.cards));
      setKeywords(data.keywords);
      setSolution(data.solution);
    } catch (err) {
      console.error("Failed to fetch game data", err);
    }
  };

  useEffect(() => {
    dragAndDropStartModalRef.current?.openModal();
  }, []);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-500 p-4 shadow-lg ring-4 ring-blue-300">
        <div className="flex items-center gap-2">
          <img className="w-12" src={HighScore} alt="" />
          <div className="min-w-5 text-lg text-white">{numberOfMoves}</div>
        </div>
        <div className="flex items-center gap-2">
          <img className="w-11" src={StarsIcon} alt="" />
          <h2 className="text-3xl font-semibold text-white">{t("drag_and_drop_game")}</h2>
          <img className="w-11" src={StarsIcon} alt="" />
        </div>
        <div className="flex items-center gap-2">
          <div className="min-w-5 text-lg text-white"></div>
          <button onClick={initializeGame} className="transition duration-300 hover:scale-110">
            <img className="w-11" src={RestartIcon} alt="" />
          </button>
        </div>
      </div>

      {/* Header Keywords */}
      <div className="mb-2 grid grid-cols-5 gap-8">
        {keywords.map((keyword) => (
          <div key={keyword.id} className="rounded-md bg-orange-400 p-2 text-center font-semibold text-white">
            {keyword.name}
          </div>
        ))}
      </div>

      {/* Drop Zones */}
      <div className="mb-4 grid grid-cols-5 gap-8">
        {keywords.map((keyword) => {
          const matchedCardId = matches[keyword.id];
          const matchedCard = cards.find((c) => c.card_id === matchedCardId);
          const isCorrect = matchedCard && checkCorrect(keyword.id, matchedCard.card_id);

          return (
            <div
              key={keyword.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(keyword.id)}
              className={`flex h-44 items-center justify-center rounded-md border-2 transition-all duration-300 ${
                matchedCard
                  ? isCorrect
                    ? "border-green-500 bg-green-100"
                    : "border-red-500 bg-red-100"
                  : "border-dashed border-orange-400"
              }`}
            >
              {matchedCard && <img src={matchedCard.image} alt="" className="h-40 p-6" />}
            </div>
          );
        })}
      </div>

      {/* Draggable Cards */}
      <div className="mb-8 grid grid-cols-5 gap-8">
        {cards
          .filter((card) => !Object.values(matches).includes(card.card_id)) // exclude matched ones
          .map((card) => (
            <img
              key={card.card_id}
              src={card.image}
              alt=""
              draggable
              onDragStart={() => setDraggingCard(card)}
              className="h-44 w-full cursor-move rounded-xl border border-gray-100 bg-white p-6 shadow-md transition hover:scale-105"
            />
          ))}
      </div>
      {/* Modals */}
      <DragAndDropGameStartModal ref={dragAndDropStartModalRef} onSubmit={initializeGame} />
      <DragAndDropGameFinishModal
        ref={dragAndDropFinishModalRef}
        onSubmit={handleGameCompletion}
        onSubmitReset={initializeGame}
        status={gameStatus}
        numberOfMoves={numberOfMoves}
        cardsCount={cards?.length}
      />
    </div>
  );
};

export default DragAndDropGame;
